import React, { Suspense, useEffect, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadRemote } from 'shell/DynamicRemote';
import { ContentSchema } from '../../definitions/schemas';
import { extractSharableIdFromUrl } from '../RoomOverview/Content/helper';
import RoomContentWrapper, {
  RoomContentWrapperProps,
} from '../Rooms/Content/RoomContentWrapper';
import useShowCover from '../../lib/hook/useShowCover';
import useFeatureFlag from '../../lib/hook/useFeatureFlag';
import useTheaterFrameHeight from '../../lib/hook/useTheaterFrameHeight';
import { OrganizationFlagEnum } from '../../lib/features/FeatureFlag';
import { getAspectRatioHeight } from '../../lib/Media';
import { getNumericSpacing } from '../../lib/Styles';
import { THEATER_FRAME_HEIGHT, UrlParams } from './helper';
import { EXTERNAL_SHARING_REMOTE_ENTRY } from '../../config/env';
import RequestConfigurationFactory from '../../Services/Request/RequestConfigurationFactory';
import OrganizationsStore from '../../stores/observables/OrganizationsStore';

const HEIGHT_OFFSET = 1.2;
const HEIGHT_OFFSET_DOWNLOADABLE = 44;

interface FederatedAssetHubViewerProps extends RoomContentWrapperProps {
  url: string;
  name?: string;
  height?: number | string;
  content: Partial<ContentSchema>;
  onWindowMessageReceived: (event: MessageEvent) => void;
  /** True when rendered inside theater mode instead of a room tile */
  forceFullScreen?: boolean;
}

const REMOTE_DETAILS = {
  remoteName: 'external_sharing',
  moduleName: './AppExposeWithoutAuth',
  url: `${EXTERNAL_SHARING_REMOTE_ENTRY}`,
};

const Loader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
    <CircularProgress color="secondary" size={40} className="loading-spinner" />
  </Box>
);

const FederatedAssetHubViewer: React.FC<FederatedAssetHubViewerProps> = (
  props: FederatedAssetHubViewerProps
): JSX.Element => {
  const {
    url,
    name,
    height,
    onWindowMessageReceived,
    cover,
    disableDefaultCover,
    rounded,
    width,
    content,
    setAutoPlay,
    forceFullScreen,
  } = props as FederatedAssetHubViewerProps & RoomContentWrapperProps;

  useEffect(() => {
    window.addEventListener('message', onWindowMessageReceived, false);
    return () => {
      window.removeEventListener('message', onWindowMessageReceived, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const theme = useTheme();
  // Width-based in room tiles; in theater mode use the measured remaining
  // space so the frame neither hides its zoom toolbar nor leaves a gap
  const { ref: theaterRef, height: theaterHeight } = useTheaterFrameHeight(
    !!forceFullScreen && height == null
  );
  const computedHeight = useMemo(
    () =>
      height ??
      (forceFullScreen
        ? theaterHeight ?? THEATER_FRAME_HEIGHT
        : (width
            ? getAspectRatioHeight(width) + HEIGHT_OFFSET_DOWNLOADABLE
            : getNumericSpacing(theme, 44)) * HEIGHT_OFFSET),
    [height, width, theme, forceFullScreen, theaterHeight]
  );

  const isInlineIframeViewerEnabled = useFeatureFlag(
    OrganizationFlagEnum.INLINE_IFRAME_VIEWER
  );

  const { showCover } = useShowCover({
    content,
    isAssetHubContent: true,
    documentCoverEditable: false,
    isInlineIframeViewerEnabled,
  });

  const { dsrData, userInteractionId } = useMemo(() => {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    const params = parsed.searchParams;
    const entries: Record<string, string | boolean> = {};
    params.forEach((value, key) => {
      if (key === UrlParams.IS_DOWNLOADABLE) {
        entries[key] = value;
        return;
      }

      if (value === 'true') {
        entries[key] = true;
      } else if (value === 'false') {
        entries[key] = false;
      } else {
        entries[key] = value;
      }
    });

    const sharableId = extractSharableIdFromUrl(parsed.toString()) || '';

    return {
      dsrData: entries,
      userInteractionId: sharableId,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatedRemoteDetails = useMemo(() => {
    let remoteDetails = {
      ...REMOTE_DETAILS,
    };

    if (RequestConfigurationFactory.isProdUs()) {
      const updatedUrl = `${EXTERNAL_SHARING_REMOTE_ENTRY.replace(
        'prod',
        'prod-us'
      )}`;
      remoteDetails = {
        ...remoteDetails,
        url: updatedUrl,
      };
    }

    return remoteDetails;
  }, []);

  const contentNode = (
    <Box
      ref={theaterRef}
      sx={{
        backgroundColor: 'white',
        border: 0,
        height: computedHeight,
        borderRadius: rounded ? '8px' : '0px',
        marginTop: '4px',
        overflow: 'hidden',
        // The federated viewer renders in our DOM: undo its min-height (it
        // overflows short frames, hiding the zoom toolbar) and keep the
        // hover-only toolbar always visible for buyers
        '&& .media-pl-wrp': { minHeight: 0 },
        '&& .pdf-container-main-layout .show-toolbar': { display: 'block' },
      }}
    >
      <Suspense fallback={<Loader />}>
        <LoadRemote
          loader={<Loader />}
          remote={updatedRemoteDetails as any}
          dsrData={dsrData}
          userInteractionId={userInteractionId}
          name={name}
          baseDomain={
            OrganizationsStore.getCurrentOrganization()?.vendorTenantUrl
          }
        />
      </Suspense>
    </Box>
  );

  return showCover ? (
    <RoomContentWrapper
      cover={cover}
      disableDefaultCover={disableDefaultCover}
      rounded={rounded}
      width={width}
      setAutoPlay={setAutoPlay as any}
    >
      {contentNode}
    </RoomContentWrapper>
  ) : (
    <Box px={3}>{contentNode}</Box>
  );
};

export default FederatedAssetHubViewer;
